// drift-test:no-test-needed: pure UI surface — captured attempts +
// operator-triggered replays + customize editor, all rendered as tabs
// in one strip. State machine: activeTab ∈ number-into-attempts | "edit".
// Backend behaviors that this UI exposes (replay endpoint, schema
// override, GCS trace lazy-fetch) are covered by orchestrator-side
// tests. T15 (trace_invariants) asserts every AttemptTrace json-tag
// is referenced in this file; the SchemaTemplateSection / PromptTemplateSection
// renderings satisfy that for prompt_template + schema_template.
import { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import type { SubCallTrace, AttemptTrace, TraceError } from "../../lib/traceApi";
import { diffLines, type DiffSegment } from "../../lib/diff";
import {
  subCallAnchorId,
  TRACE_ANCHOR_FOCUS_EVENT,
} from "../../lib/traceAnchors";
import { JsonViewer } from "./JsonViewer";
import { useTraceLive } from "./TraceLiveContext";
import { replaySubCall } from "../../api/traceApi";

// Shared placeholder shown in every detail section while the run is live.
// Full attempt detail (prompt, response, parsed output, tokens, cost) is
// only assembled + persisted at run-end (single WriteDiagnostics in the
// runner's defer), so during the live window these fields are empty by
// design. The placeholder says so rather than reading as "(empty)" /
// "not captured" — which would imply something broke.
function RunEndPending({ what }: { what: string }) {
  return (
    <span className="text-gray-500 italic text-[11px]">
      ⧗ {what} is captured when the run completes — not yet available on the
      live stream.
    </span>
  );
}

// ExpandPanel is the operator-facing detail surface for one sub-call.
// Layout (commit 4f0a3b7+ refactor):
//   1. Attempt selector (when 2+ attempts)
//   2. Metadata strip: engine config + content hashes + blob outcome
//   3. Prompt template (collapsible) — what the LLM was asked to do
//   4. Side-by-side: Packet (input JSON) | Response (raw LLM output) |
//      Parsed (validated structured output). Each rendered via
//      JsonViewer for navigable trees.
//   5. Validation panel — always visible, color-coded by attempt outcome
//   6. Diff (1 vs 2) — collapsed by default; only present when 2+ attempts
//
// Replaces the prior tab-based layout where "prompt" mis-rendered the
// packet, "state" was a placeholder string, and the operator had to
// click through tabs sequentially to follow Packet → Response → Parsed.

export function ExpandPanel({
  subCall,
  parentStageErrors,
  referenceSubCall,
}: {
  subCall: SubCallTrace;
  // Errors from RunTrace.Errors that pertain to this sub-call's parent
  // stage (already filtered + attempt-bound by StageRow). Used by the
  // Validation panel to explain why the run failed when this sub-call
  // itself passed validation — answer-via-bridging.
  parentStageErrors?: TraceError[];
  // Compare-mode: the matching sub-call from the pinned reference run.
  // When present, the panel adds a "Compare to reference" section
  // showing the parsed_output diff side-by-side. (Item 6 / §7.8.5.)
  referenceSubCall?: SubCallTrace | null;
}) {
  // activeTab is "edit" when the operator is in the customize editor;
  // otherwise it's an index into combined attempts (captured + replays).
  const [activeTab, setActiveTab] = useState<number | "edit">(0);
  // Tracks the last non-edit tab the operator was viewing. Used by
  // openEditTab (seed from the focused attempt), runReplay (target the
  // attempt that was focused, not the implicit 0 fallback), and
  // closeEditTab (return focus to where the operator came from). Without
  // this, opening Edit from Attempt 2 then clicking the outer Replay
  // button would re-run Attempt 1 instead of Attempt 2.
  const lastAttemptIdxRef = useRef<number>(0);
  useEffect(() => {
    if (typeof activeTab === "number") {
      lastAttemptIdxRef.current = activeTab;
    }
  }, [activeTab]);
  const [showDiff, setShowDiff] = useState(false);
  // Replays accumulated this session (per-mount). Appended to the captured
  // attempts for rendering, so each replay gets its own "Replay N" tab and
  // re-uses the same panel layout (prompt template / schema / packet /
  // response / parsed / validation).
  const [replays, setReplays] = useState<AttemptTrace[]>([]);
  // Edit tab visibility — once opened, persists until the operator
  // explicitly closes it (clicking the × on the tab). Closing discards
  // the in-progress overrides; the operator can re-open Edit any time
  // and the seeds come from the currently-focused attempt.
  const [editTabOpen, setEditTabOpen] = useState(false);
  const [promptOverride, setPromptOverride] = useState("");
  const [packetOverride, setPacketOverride] = useState("");
  const [schemaOverride, setSchemaOverride] = useState("");
  // Engine the next Customize replay runs on. Seeded from the focused
  // attempt's adapter_name when Edit opens; the plain Replay button leaves
  // this unused (engine="" → server replays on the original attempt's
  // engine). Lets the operator A/B codex vs claude on the same inputs.
  const [engineOverride, setEngineOverride] = useState("");
  const [replayState, setReplayState] = useState<
    | { kind: "idle" }
    | { kind: "running" }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  const { uid = "", pid = "", rid = "" } = useParams();
  const { live } = useTraceLive();
  const capturedAttempts = subCall.attempts ?? [];
  const attempts = [...capturedAttempts, ...replays];
  const tabIsEdit = activeTab === "edit";
  // When viewing the Edit tab, fall back to the last focused attempt
  // (not 0) so the panel's behavior is anchored on the operator's
  // mental model of "I was looking at attempt N, then opened Edit."
  const attemptIdx = typeof activeTab === "number" ? activeTab : lastAttemptIdxRef.current;
  const attempt = attempts[attemptIdx];
  const hasRetry = capturedAttempts.length >= 2;
  if (!attempt) return null;
  const isReplay = !tabIsEdit && attemptIdx >= capturedAttempts.length;
  const replayOrdinal = isReplay ? attemptIdx - capturedAttempts.length + 1 : 0;

  function tabLabel(i: number): string {
    if (i < capturedAttempts.length) return `Attempt ${i + 1}`;
    return `Replay ${i - capturedAttempts.length + 1}`;
  }

  function openEditTab() {
    if (!editTabOpen) {
      // Seed overrides from the currently-focused attempt — saves the
      // operator from copy-pasting the captured values.
      const seed = attempts[attemptIdx];
      setPromptOverride(seed?.prompt_template ?? "");
      setPacketOverride(seed?.prompt_sent ?? "");
      setSchemaOverride(seed?.schema_template ?? "");
      setEngineOverride(seed?.adapter_name ?? "");
      setEditTabOpen(true);
    }
    setActiveTab("edit");
  }

  function closeEditTab() {
    setEditTabOpen(false);
    if (tabIsEdit) {
      // Return to the last non-edit tab the operator was viewing. Clamp
      // to a valid index (defensive — attempts.length is always ≥1 here
      // because the early `if (!attempt) return null` guard ran).
      const target = Math.min(lastAttemptIdxRef.current, attempts.length - 1);
      setActiveTab(Math.max(0, target));
    }
  }

  async function runReplay(opts: { withOverrides: boolean }) {
    setReplayState({ kind: "running" });
    // attemptIndex param targets a CAPTURED attempt — replay always
    // pivots off original data. Clamp to the last captured attempt
    // when the operator is viewing a Replay tab or the Edit tab.
    const targetIdx = Math.min(attemptIdx, capturedAttempts.length - 1);
    try {
      const resp = await replaySubCall({
        uid,
        pid,
        rid,
        subPromptId: subCall.sub_prompt_id,
        itemKey: subCall.item_key,
        attemptIndex: Math.max(0, targetIdx) + 1,
        overridePrompt: opts.withOverrides && promptOverride ? promptOverride : undefined,
        overridePacket: opts.withOverrides && packetOverride ? packetOverride : undefined,
        overrideSchema: opts.withOverrides && schemaOverride ? schemaOverride : undefined,
        // Plain Replay leaves engine empty → server replays on the original
        // attempt's engine (faithful). Customize replay sends the chosen
        // engine so the operator can A/B codex vs claude.
        engine: opts.withOverrides && engineOverride ? engineOverride : undefined,
      });
      const replayAttempts = (resp.attempts ?? []) as unknown as AttemptTrace[];
      if (replayAttempts.length > 0) {
        setReplays((prev) => {
          const next = [...prev, replayAttempts[0]];
          setActiveTab(capturedAttempts.length + next.length - 1);
          return next;
        });
        setReplayState({ kind: "idle" });
      } else {
        setReplayState({ kind: "error", message: "replay returned no attempt data" });
      }
    } catch (e: unknown) {
      setReplayState({ kind: "error", message: String(e) });
    }
  }

  return (
    <div className="border border-gray-700 rounded bg-gray-950 p-2">
      {/* Section 1: Attempt selector + Edit tab + actions */}
      <div className="flex items-center gap-2 mb-2 border-b border-gray-700 flex-wrap">
        {attempts.map((_, i) => {
          const isR = i >= capturedAttempts.length;
          const active = !tabIsEdit && activeTab === i;
          return (
            <button
              key={i}
              onClick={() => setActiveTab(i)}
              className={`px-2 py-1 text-xs ${active ? "bg-gray-700" : ""} ${isR ? "text-amber-300" : ""}`}
              title={isR ? "Replay (operator-triggered, not persisted in the run)" : undefined}
            >
              {tabLabel(i)}
            </button>
          );
        })}
        {editTabOpen && (
          <button
            onClick={() => setActiveTab("edit")}
            className={`px-2 py-1 text-xs inline-flex items-center gap-1 ${tabIsEdit ? "bg-amber-900/40" : ""} text-amber-300 border border-amber-700/50 rounded`}
            title="Customize editor — modify prompt / packet / schema, then replay"
          >
            ✎ Edit
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                closeEditTab();
              }}
              className="ml-1 text-amber-400 hover:text-amber-100"
              title="Close Edit tab (discards in-progress overrides)"
            >
              ×
            </span>
          </button>
        )}
        <button
          onClick={() => void runReplay({ withOverrides: false })}
          disabled={replayState.kind === "running" || live}
          title={
            live
              ? "Available after the run completes — replay needs the captured attempt input, which is persisted at run-end."
              : "Re-run this sub-call against a fresh adapter with the captured input (does NOT modify the persisted run)."
          }
          className="text-[10px] text-amber-300 hover:text-amber-100 px-2 py-1 border border-amber-700/50 rounded disabled:opacity-50"
        >
          {replayState.kind === "running" ? "↻ replaying…" : "↻ Replay"}
        </button>
        <button
          onClick={openEditTab}
          disabled={live}
          title={
            live
              ? "Available after the run completes — the editor seeds from the captured prompt / packet / schema, which are persisted at run-end."
              : "Open the Edit tab: edit prompt / packet / schema, then replay to compare outputs."
          }
          className={`text-[10px] px-2 py-1 border rounded disabled:opacity-50 ${editTabOpen ? "bg-amber-950/40 border-amber-700/50 text-amber-100" : "border-amber-700/50 text-amber-300 hover:text-amber-100"}`}
        >
          ✎ Customize
        </button>
        {replayState.kind === "error" && (
          <span className="text-[10px] text-rose-300 ml-1" title={replayState.message}>
            ✗ {replayState.message.length > 60 ? replayState.message.slice(0, 60) + "…" : replayState.message}
            <button
              onClick={() => setReplayState({ kind: "idle" })}
              className="ml-1 text-rose-400 hover:text-rose-100"
              title="Dismiss"
            >
              ×
            </button>
          </span>
        )}
        <button
          onClick={() => {
            void navigator.clipboard.writeText(JSON.stringify(subCall, null, 2));
          }}
          title="Copy the whole sub-call as JSON (useful for bug reports)"
          className="text-[10px] text-gray-400 hover:text-gray-100 px-2 py-1 border border-gray-700 rounded ml-auto"
        >
          ⧉ Copy sub-call JSON
        </button>
      </div>

      {tabIsEdit ? (
        <EditTabBody
          promptOverride={promptOverride}
          onPromptChange={setPromptOverride}
          packetOverride={packetOverride}
          onPacketChange={setPacketOverride}
          schemaOverride={schemaOverride}
          onSchemaChange={setSchemaOverride}
          engineOverride={engineOverride}
          onEngineChange={setEngineOverride}
          originalPrompt={attempts[Math.min(attemptIdx, capturedAttempts.length - 1)]?.prompt_template ?? ""}
          originalPacket={attempts[Math.min(attemptIdx, capturedAttempts.length - 1)]?.prompt_sent ?? ""}
          originalSchema={attempts[Math.min(attemptIdx, capturedAttempts.length - 1)]?.schema_template ?? ""}
          originalEngine={attempts[Math.min(attemptIdx, capturedAttempts.length - 1)]?.adapter_name ?? ""}
          onReplay={() => void runReplay({ withOverrides: true })}
          replaying={replayState.kind === "running"}
        />
      ) : (
        <AttemptBody
          attempt={attempt}
          subCall={subCall}
          isReplay={isReplay}
          replayOrdinal={replayOrdinal}
          activeAttempt={attemptIdx}
          capturedCount={capturedAttempts.length}
          parentStageErrors={parentStageErrors}
          referenceSubCall={referenceSubCall}
          hasRetry={hasRetry}
          attempts={attempts}
          showDiff={showDiff}
          setShowDiff={setShowDiff}
          live={live}
        />
      )}
    </div>
  );
}

// AttemptBody renders the per-attempt sections: metadata, prompt template,
// schema, packet/response/parsed columns, validation, compare-to-reference,
// diff. Extracted from ExpandPanel so the Edit tab can replace it cleanly.
function AttemptBody({
  attempt,
  subCall,
  isReplay,
  replayOrdinal,
  activeAttempt,
  capturedCount,
  parentStageErrors,
  referenceSubCall,
  hasRetry,
  attempts,
  showDiff,
  setShowDiff,
  live,
}: {
  attempt: AttemptTrace;
  subCall: SubCallTrace;
  isReplay: boolean;
  replayOrdinal: number;
  activeAttempt: number;
  capturedCount: number;
  parentStageErrors?: TraceError[];
  referenceSubCall?: SubCallTrace | null;
  hasRetry: boolean;
  attempts: AttemptTrace[];
  showDiff: boolean;
  setShowDiff: (v: boolean) => void;
  // True while the run is in flight (reducer is the source). Detail
  // fields aren't on the live stream — see RunEndPending.
  live: boolean;
}) {
  return (
    <>

      {/* Section 2: Metadata strip */}
      <MetadataStrip attempt={attempt} subCall={subCall} />

      {/* Section 3: Prompt template */}
      <PromptTemplateSection attempt={attempt} subCall={subCall} live={live} />

      {/* Section 3b: Output schema */}
      <SchemaTemplateSection attempt={attempt} subCall={subCall} live={live} />

      {/* Section 4: Three-column data flow */}
      <div className="grid grid-cols-3 gap-2 mb-2">
        <DataColumn title="Packet (sent)" subtitle="state JSON the LLM received">
          <PacketPanel attempt={attempt} live={live} />
        </DataColumn>
        <DataColumn title="Response (raw)" subtitle="LLM output verbatim">
          <ResponsePanel attempt={attempt} live={live} />
        </DataColumn>
        <DataColumn title="Parsed" subtitle="post-schema-validation">
          <ParsedPanel attempt={attempt} live={live} />
        </DataColumn>
      </div>

      {/* Section 5: Validation */}
      <div className="mb-2">
        {isReplay && (
          <div className="text-[10px] text-amber-400 mb-1 italic">
            ↻ Replay {replayOrdinal} (operator-triggered, NOT persisted in this
            run's RunTrace). Re-runs against a fresh adapter call.
          </div>
        )}
        <ValidationTab
          attempt={attempt}
          subCall={subCall}
          isFinalAttempt={activeAttempt === capturedCount - 1}
          parentStageErrors={parentStageErrors}
          live={live}
        />
      </div>

      {/* Section 6: Compare to reference run (item 6 / §7.8.5). */}
      {referenceSubCall && (
        <CompareToReferenceSection
          current={attempt}
          referenceAttempt={(referenceSubCall.attempts ?? []).slice(-1)[0]}
        />
      )}

      {/* Section 7: Diff (1 vs 2) — collapsible */}
      {hasRetry && (
        <details
          open={showDiff}
          onToggle={(e) => setShowDiff((e.target as HTMLDetailsElement).open)}
          className="border border-gray-800 rounded"
        >
          <summary className="cursor-pointer text-xs px-2 py-1 bg-gray-900 hover:bg-gray-800">
            Diff (attempt 1 vs attempt 2)
          </summary>
          <div className="font-mono text-xs whitespace-pre-wrap max-h-96 overflow-y-auto bg-black p-2">
            <AttemptDiffView left={attempts[0]} right={attempts[1]} />
          </div>
        </details>
      )}
    </>
  );
}

// MetadataStrip renders the per-attempt config + observability fields
// in a compact one-line row above the prompt section. Surfaces engine
// info, content hashes, and blob-write status — all carried in
// AttemptTrace but previously never rendered.
function MetadataStrip({
  attempt,
  subCall,
}: {
  attempt: AttemptTrace;
  subCall: SubCallTrace;
}) {
  const engine = [attempt.adapter_name, attempt.model_name, attempt.effort_level]
    .filter(Boolean)
    .join("/");
  const tokens =
    attempt.input_tokens != null && attempt.output_tokens != null
      ? `${attempt.input_tokens}→${attempt.output_tokens} tok`
      : "—";
  const cost = attempt.cost_usd != null ? `$${attempt.cost_usd.toFixed(4)}` : "—";
  // Prompt-cache hit rate. nil cached_input_tokens = CLI didn't expose; show "—".
  // 0 = no hit (cold prefix). N>0 with input_tokens M shows N/M (XX%).
  const cache =
    attempt.cached_input_tokens != null && attempt.input_tokens != null && attempt.input_tokens > 0
      ? `${attempt.cached_input_tokens}/${attempt.input_tokens} (${Math.round(
          (attempt.cached_input_tokens / attempt.input_tokens) * 100,
        )}%)`
      : attempt.cached_input_tokens != null
        ? `${attempt.cached_input_tokens}`
        : null;
  const blobOutcome = attempt.prompt_sent_write_outcome || "—";
  const blobOutcomeColor =
    blobOutcome === "persisted"
      ? "text-emerald-300"
      : blobOutcome === "upload_failed_truncated_inline"
        ? "text-amber-300"
        : blobOutcome === "lifecycle_deleted"
          ? "text-gray-500"
          : "text-gray-400";

  return (
    <div className="text-[10px] font-mono text-gray-400 mb-2 px-2 py-1 bg-gray-900 rounded flex flex-wrap gap-x-3 gap-y-1">
      <span>
        Attempt {attempt.attempt} ·{" "}
        <span className="text-gray-300">{(attempt.duration_ms / 1000).toFixed(2)}s</span> ·{" "}
        {tokens} · {cost}
      </span>
      {/* drift-test:no-test-needed: presentational badge; field contract enforced by T15 (orchestrator contract tests) */}
      {attempt.reused && (
        <span title="Answer stored earlier in this pass; no new tokens" className="text-gray-300">
          reused
        </span>
      )}
      {engine && (
        <span title="adapter/model/effort">
          engine: <span className="text-gray-300">{engine}</span>
        </span>
      )}
      {/* drift-test:no-test-needed: presentational badge; field contract enforced by T15 (orchestrator contract tests) */}
      {attempt.num_turns != null && attempt.num_turns > 0 && (
        <span title="Internal CLI API turns for this call (claude --json-schema structured output is 2 turns by design; each turn re-reads the conversation, mostly as cache reads). Absent on codex.">
          turns: <span className="text-gray-300">{attempt.num_turns}</span>
        </span>
      )}
      {cache && (
        <span title="Hit in the provider's prompt cache (cached_input_tokens / input_tokens). Cached input is priced at the model's cached-input rate, a fraction of the input rate.">
          cache: <span className="text-gray-300">{cache}</span>
        </span>
      )}
      {attempt.prompt_sha && (
        <span title={`Full prompt SHA: ${attempt.prompt_sha}`}>
          prompt_sha: <span className="text-gray-300">{attempt.prompt_sha.slice(0, 12)}</span>
        </span>
      )}
      {attempt.schema_sha && (
        <span title={`Full schema SHA: ${attempt.schema_sha}`}>
          schema_sha: <span className="text-gray-300">{attempt.schema_sha.slice(0, 12)}</span>
        </span>
      )}
      <span>
        blob: <span className={blobOutcomeColor}>{blobOutcome}</span>
      </span>
      {subCall.prompt_file && (
        <span title="path to prompt file">
          file: <span className="text-gray-300">{subCall.prompt_file}</span>
        </span>
      )}
    </div>
  );
}

// PromptTemplateSection renders the prompt-template markdown body
// inside a <details> block (collapsed by default — operators usually
// scan packet/response/parsed first, then dive into the prompt only
// when those don't explain the output). Falls back to "file path only"
// when prompt_template wasn't captured (older runs OR
// orchestrator-side read failure).
function PromptTemplateSection({
  attempt,
  subCall,
  live,
}: {
  attempt: AttemptTrace;
  subCall: SubCallTrace;
  live: boolean;
}) {
  const template = attempt.prompt_template ?? "";
  return (
    <details className="border border-gray-800 rounded mb-2">
      <summary className="cursor-pointer text-xs px-2 py-1 bg-gray-900 hover:bg-gray-800 text-gray-200">
        Prompt template
        {!template && live && (
          <span className="ml-2 text-gray-500 text-[10px]">(at run-end)</span>
        )}
        {!template && !live && subCall.prompt_file && (
          <span className="ml-2 text-gray-500 text-[10px]">
            (not captured; file: {subCall.prompt_file})
          </span>
        )}
      </summary>
      <div className="font-mono text-xs whitespace-pre-wrap max-h-96 overflow-y-auto bg-black p-2 text-gray-200">
        {template ? (
          template
        ) : live ? (
          <RunEndPending what="Prompt template" />
        ) : (
          <span className="text-gray-500 italic">
            (prompt template not captured for this attempt — pre-refactor
            run, or orchestrator couldn't read the file. Verify against{" "}
            <span className="font-mono text-gray-300">{subCall.prompt_file}</span>{" "}
            at prompt_sha {attempt.prompt_sha?.slice(0, 12) || "—"}.)
          </span>
        )}
      </div>
    </details>
  );
}

// SchemaTemplateSection mirrors PromptTemplateSection for the output
// schema. Same collapse-by-default UX, same fallback message when the
// schema body wasn't captured (older runs or read failure).
function SchemaTemplateSection({
  attempt,
  subCall,
  live,
}: {
  attempt: AttemptTrace;
  subCall: SubCallTrace;
  live: boolean;
}) {
  const schema = attempt.schema_template ?? "";
  return (
    <details className="border border-gray-800 rounded mb-2">
      <summary className="cursor-pointer text-xs px-2 py-1 bg-gray-900 hover:bg-gray-800 text-gray-200">
        Output schema
        {!schema && live && (
          <span className="ml-2 text-gray-500 text-[10px]">(at run-end)</span>
        )}
        {!schema && !live && subCall.schema_file && (
          <span className="ml-2 text-gray-500 text-[10px]">
            (not captured; file: {subCall.schema_file})
          </span>
        )}
      </summary>
      <div className="font-mono text-xs whitespace-pre-wrap max-h-96 overflow-y-auto bg-black p-2 text-gray-200">
        {schema ? (
          schema
        ) : live ? (
          <RunEndPending what="Output schema" />
        ) : (
          <span className="text-gray-500 italic">
            (output schema not captured for this attempt — pre-refactor
            run, or orchestrator couldn't read the file. Verify against{" "}
            <span className="font-mono text-gray-300">{subCall.schema_file}</span>{" "}
            at schema_sha {attempt.schema_sha?.slice(0, 12) || "—"}.)
          </span>
        )}
      </div>
    </details>
  );
}

function DataColumn({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-gray-800 rounded overflow-hidden">
      <div className="px-2 py-1 bg-gray-900 text-xs">
        <div className="font-bold text-gray-200">{title}</div>
        <div className="text-[10px] text-gray-500">{subtitle}</div>
      </div>
      <div className="bg-black p-2 max-h-96 overflow-auto">{children}</div>
    </div>
  );
}

function PacketPanel({ attempt, live }: { attempt: AttemptTrace; live: boolean }) {
  if (attempt.prompt_sent_ref) {
    return (
      <div className="text-xs text-gray-400">
        [GCS overflow — fetch via /trace/blob]
        <div className="text-[10px] text-gray-500 mt-1 break-all">{attempt.prompt_sent_ref}</div>
      </div>
    );
  }
  if (live && !attempt.prompt_sent) return <RunEndPending what="Packet (sent)" />;
  const parsed = tryParseJSON(attempt.prompt_sent);
  if (parsed === undefined) {
    return (
      <pre className="text-xs text-gray-300 whitespace-pre-wrap">{attempt.prompt_sent || "(empty)"}</pre>
    );
  }
  return <JsonViewer value={parsed} defaultExpandDepth={1} />;
}

function ResponsePanel({ attempt, live }: { attempt: AttemptTrace; live: boolean }) {
  if (attempt.raw_response_ref) {
    return (
      <div className="text-xs text-gray-400">
        [GCS overflow — fetch via /trace/blob]
        <div className="text-[10px] text-gray-500 mt-1 break-all">{attempt.raw_response_ref}</div>
      </div>
    );
  }
  if (live && !attempt.raw_response) return <RunEndPending what="Response (raw)" />;
  const parsed = tryParseJSON(attempt.raw_response);
  if (parsed === undefined) {
    return (
      <pre className="text-xs text-gray-300 whitespace-pre-wrap">{attempt.raw_response || "(empty)"}</pre>
    );
  }
  return <JsonViewer value={parsed} defaultExpandDepth={1} />;
}

function ParsedPanel({ attempt, live }: { attempt: AttemptTrace; live: boolean }) {
  if (attempt.parsed_output_ref) {
    return (
      <div className="text-xs text-gray-400">
        [GCS overflow — fetch via /trace/blob]
        <div className="text-[10px] text-gray-500 mt-1 break-all">{attempt.parsed_output_ref}</div>
      </div>
    );
  }
  if (live && attempt.parsed_output == null) return <RunEndPending what="Parsed output" />;
  return <JsonViewer value={attempt.parsed_output} defaultExpandDepth={2} />;
}

function tryParseJSON(s: string): unknown | undefined {
  if (!s) return undefined;
  try {
    return JSON.parse(s);
  } catch {
    return undefined;
  }
}

// CascadeOriginLink renders the "cancelled by → <originator>" affordance
// shown on canceled sub-calls. The link target is the originator
// sub-call's anchor (subCallAnchorId); clicking scrolls + expands that
// row via the TRACE_ANCHOR_FOCUS_EVENT bus, mirroring how the run-level
// errors panel + outlier-row links navigate. Renders nothing when the
// stamp is empty (degenerate case: no abort sibling in the layer).
function CascadeOriginLink({ subCall }: { subCall: SubCallTrace }) {
  const origID = subCall.originator_sub_prompt_id ?? "";
  const origKey = subCall.originator_item_key ?? "";
  if (origID === "") return null;
  const anchor = subCallAnchorId({
    ...subCall,
    sub_prompt_id: origID,
    item_key: origKey,
  });
  return (
    <div className="mt-2 text-[12px] text-amber-200/90">
      ↳ Cancelled by{" "}
      <a
        href={`#${anchor}`}
        onClick={(e) => {
          // Belt + suspenders: dispatch focus event so the target row
          // expands even if it's already mounted (hash-change alone
          // wouldn't re-trigger the SubCallRow auto-expand listener).
          window.dispatchEvent(
            new CustomEvent(TRACE_ANCHOR_FOCUS_EVENT, { detail: anchor }),
          );
          // Let the browser still set the hash for permalink + scroll.
          // No preventDefault.
          void e;
        }}
        className="font-mono underline text-amber-100 hover:text-amber-50"
      >
        {origID}
        {origKey ? `[${origKey}]` : ""}
      </a>
      {" "}— that's the originating failure carrying the actual root-cause error.
    </div>
  );
}

// AttemptTabContent + the "Tab" type were retired in the side-by-side
// refactor — Packet / Response / Parsed now render simultaneously as
// columns via PacketPanel / ResponsePanel / ParsedPanel above, and the
// "state" placeholder tab is gone (it was a duplicate of Packet).

// ValidationTab is informative for ALL three cases:
//   1. This attempt's per-call validation passed (no feedback, no error)
//      → affirmative "✓ Passed JSON parse · ✓ Passed semantic validation"
//      → if parent stage has errors, bridge: "this sub-call passed; the
//         parent stage attempt failed for these higher-scope reasons"
//   2. This attempt's per-call validation failed (feedback / error set)
//      → structured display of feedback_kind + feedback + error
//      → "retried" badge when there's a next attempt
//   3. This attempt produced an adapter error (only error set)
//      → highlight as transport failure
//
// The empty-state "—" placeholder from the previous design was correct
// in the sense that "no failure happened at this scope" — but
// uninformative. Operators opening a sub-call in a failed run want to
// know either why THIS sub-call failed (when it did) or where to look
// for the actual failure (when it didn't).
function ValidationTab({
  attempt,
  subCall,
  isFinalAttempt,
  parentStageErrors,
  live,
}: {
  attempt: AttemptTrace;
  subCall: SubCallTrace;
  isFinalAttempt: boolean;
  parentStageErrors?: TraceError[];
  live: boolean;
}) {
  const subCallOutcome = subCall.outcome;
  const hasFeedback = attempt.feedback !== "";
  const hasError = attempt.error !== "";
  const stageErrors = parentStageErrors ?? [];

  // Pending path: the sub-call has no recorded outcome yet AND this
  // attempt produced no feedback/error. The success path below treats
  // "no feedback + no error" as "passed validation" — but a sub-call
  // that's still running (live) or one left unsealed when the run died
  // (terminal) hasn't passed anything. Without this guard the panel
  // falsely reads "✓ Passed per-call validation" for an in-flight call.
  if (subCallOutcome === "" && !hasFeedback && !hasError) {
    return (
      <div className="bg-gray-900 border border-gray-700 p-2">
        {live ? (
          <>
            <div className="text-amber-300 font-bold">⧗ Running — result pending</div>
            <div className="text-gray-400 text-[11px] mt-1">
              Validation outcome (pass / retry / fallback / abort) and the
              prompt / response / parsed-output detail are recorded when the
              run completes.
            </div>
          </>
        ) : (
          <>
            <div className="text-amber-300 font-bold">⧗ Incomplete — no outcome recorded</div>
            <div className="text-gray-400 text-[11px] mt-1">
              This sub-call never sealed an outcome — the run ended (failed or
              was interrupted) before it finished. Check the stage / run errors
              above for where the run stopped.
            </div>
          </>
        )}
      </div>
    );
  }

  // Failure path: this attempt produced a feedback or error string.
  if (hasFeedback || hasError) {
    const isCancel = subCallOutcome === "canceled";
    // Distinguish cascade-cancel from genuine adapter failure. Cancel is
    // a yellow advisory ("downstream of someone else's failure"); abort
    // is red ("this sub-call's own attempts terminally failed").
    const kindLabel = isCancel
      ? "Cascade-cancelled (errgroup tore down after a sibling failed first)"
      : attempt.feedback_kind === "schema"
        ? "Schema validation failed (JSON parse or output-schema mismatch)"
        : attempt.feedback_kind === "semantic"
        ? "Semantic validation failed (ValidateOutput rejected)"
        : hasError && !hasFeedback
        ? "Adapter / transport error (no LLM response to validate)"
        : "Validation failed";
    const panelClass = isCancel
      ? "bg-amber-950/30 border border-amber-700/40 p-2"
      : "bg-rose-950/40 border border-rose-700/40 p-2";
    const titleClass = isCancel ? "text-amber-300 font-bold" : "text-rose-300 font-bold";
    const bodyClass = isCancel ? "text-amber-100" : "text-rose-100";
    return (
      <div className="space-y-2">
        <div className={panelClass}>
          <div className={titleClass}>{kindLabel}</div>
          {isCancel && <CascadeOriginLink subCall={subCall} />}
          {hasFeedback && (
            <div className="mt-2">
              <div className="text-gray-400 text-[10px] uppercase mb-1">Feedback fed back to the LLM</div>
              <div className={bodyClass}>{attempt.feedback}</div>
            </div>
          )}
          {hasError && (
            <div className="mt-2">
              <div className="text-gray-400 text-[10px] uppercase mb-1">Error</div>
              <div className={bodyClass}>{attempt.error}</div>
            </div>
          )}
        </div>
        {!isFinalAttempt && (
          <div className="text-amber-300 text-[11px]">
            ↳ This attempt was retried. See attempt {attempt.attempt + 1}.
          </div>
        )}
        {isFinalAttempt && subCallOutcome === "abort" && (
          <div className="text-rose-400 text-[11px]">
            ↳ Final attempt; retry budget exhausted. Sub-call outcome: <span className="font-mono">{subCallOutcome}</span>.
          </div>
        )}
        {isFinalAttempt && subCallOutcome === "fallback" && (
          <div className="text-amber-400 text-[11px]">
            ↳ Final attempt failed; sub-call used DeterministicFallback. The synthesized fallback flows downstream.
          </div>
        )}
        {isFinalAttempt && subCallOutcome === "canceled" && (
          <div className="text-ink-400 text-[11px]">
            ↳ This sub-call did no work of its own — it was cancelled mid-flight when an earlier sibling failed.
          </div>
        )}
      </div>
    );
  }

  // Success path: this attempt passed per-call validation. Show explicit
  // affirmative state; surface parent-stage errors when present so the
  // user knows where to look if the run still failed.
  return (
    <div className="space-y-2">
      <div className="bg-emerald-950/40 border border-emerald-700/40 p-2">
        <div className="text-emerald-300 font-bold">✓ Passed per-call validation</div>
        <ul className="mt-1 text-emerald-100/80 text-[11px] space-y-0.5">
          <li>· JSON parse: ok</li>
          <li>· ValidateOutput (semantic): ok</li>
          {attempt.adapter_name && (
            <li className="text-gray-500">
              · Adapter: {attempt.adapter_name}
              {attempt.model_name ? ` · ${attempt.model_name}` : ""}
              {attempt.effort_level ? ` · effort=${attempt.effort_level}` : ""}
            </li>
          )}
        </ul>
      </div>
      {stageErrors.length > 0 && (
        <div className="bg-amber-950/40 border-l-2 border-amber-600 p-2">
          <div className="text-amber-300 text-[10px] uppercase mb-1">
            Parent stage failed at a higher scope ({stageErrors.length}{" "}
            error{stageErrors.length === 1 ? "" : "s"})
          </div>
          <div className="text-amber-100/90 text-[11px] mb-2">
            This sub-call passed its per-call validation. The parent stage's
            attempt failed at the runner-level (post-DAG validator, apply, or
            invariant check). Per-call validation only catches schema and
            ValidateOutput failures inside an individual LLM call — wider
            failure modes surface here:
          </div>
          <ul className="space-y-1">
            {stageErrors.map((e, i) => (
              <li key={i} className="font-mono text-[11px]">
                <span className="text-amber-400">[{e.source}/{e.code}]</span>{" "}
                <span className="text-amber-100">{e.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {stageErrors.length === 0 && subCallOutcome === "success" && (
        <div className="text-gray-500 text-[11px]">
          ↳ Sub-call outcome: <span className="font-mono">success</span>. If
          the run still failed, check the stage row — failures outside the
          per-call validator scope (post-DAG applies, stage Tx, etc.) live
          there, not on individual sub-calls.
        </div>
      )}
    </div>
  );
}

// CompareToReferenceSection renders this attempt's parsed_output
// side-by-side with the matching reference attempt's parsed_output.
// Pretty-prints both with sorted keys so the diff is structural, not
// noisy key-order. (Item 6 of the trace-view refactor / §7.8.5.)
function CompareToReferenceSection({
  current,
  referenceAttempt,
}: {
  current: AttemptTrace;
  referenceAttempt?: AttemptTrace;
}) {
  if (!referenceAttempt) {
    return (
      <details className="border border-amber-700/40 rounded mb-2">
        <summary className="cursor-pointer text-xs px-2 py-1 bg-amber-950/30 text-amber-200">
          Compare to reference run
        </summary>
        <div className="bg-black p-2 text-xs text-amber-100">
          Reference run pinned but has no matching sub-call (sub_prompt_id +
          item_key) — likely this sub-call was added since the reference run,
          or item-key minting drifted.
        </div>
      </details>
    );
  }
  const leftPretty = prettyJSON(JSON.stringify(referenceAttempt.parsed_output));
  const rightPretty = prettyJSON(JSON.stringify(current.parsed_output));
  return (
    <details className="border border-amber-700/40 rounded mb-2" open>
      <summary className="cursor-pointer text-xs px-2 py-1 bg-amber-950/30 text-amber-200">
        Compare to reference run (current vs reference parsed_output)
      </summary>
      <div className="bg-black p-2">
        <DiffSection
          label="parsed_output (reference ← → current)"
          left={leftPretty}
          right={rightPretty}
        />
      </div>
    </details>
  );
}

// CustomizeReplayEditor renders the prompt-template + state-packet
// textareas. Rendered absolutely positioned so it floats over the panel
// without disrupting the layout grid. Reset buttons restore the captured
// values per field. Submit re-runs with whichever fields differ from the
// originals (server treats empty-string as "no override").
// EditTabBody renders the customize-replay editor INLINE inside the
// ExpandPanel body (replacing the per-attempt sections) when activeTab
// is "edit". Pre-this-refactor the editor was a floating absolute-
// positioned popup; treating it as a tab integrates it with the same
// strip the captured attempts + replays use, matching the operator's
// mental model of "input surface for the next replay."
function EditTabBody({
  promptOverride,
  onPromptChange,
  packetOverride,
  onPacketChange,
  schemaOverride,
  onSchemaChange,
  engineOverride,
  onEngineChange,
  originalPrompt,
  originalPacket,
  originalSchema,
  originalEngine,
  onReplay,
  replaying,
}: {
  promptOverride: string;
  onPromptChange: (v: string) => void;
  packetOverride: string;
  onPacketChange: (v: string) => void;
  schemaOverride: string;
  onSchemaChange: (v: string) => void;
  engineOverride: string;
  onEngineChange: (v: string) => void;
  originalPrompt: string;
  originalPacket: string;
  originalSchema: string;
  originalEngine: string;
  onReplay: () => void;
  replaying: boolean;
}) {
  const promptDirty = promptOverride !== originalPrompt;
  const packetDirty = packetOverride !== originalPacket;
  const schemaDirty = schemaOverride !== originalSchema;
  const engineDirty = engineOverride !== originalEngine;
  const anyDirty = promptDirty || packetDirty || schemaDirty || engineDirty;
  return (
    <div className="bg-gray-950 p-1">
      <div className="text-amber-300 text-xs font-bold mb-2">
        ✎ Customize replay
        <span className="ml-2 text-amber-200/60 text-[10px] font-normal italic">
          Edit and re-run; does NOT modify the persisted run. Output lands as a new Replay tab.
        </span>
      </div>
      <div className="space-y-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-gray-400 text-[10px] uppercase">Engine</span>
            {engineDirty && (
              <span className="text-amber-300 text-[10px]">● changed from {originalEngine || "default"}</span>
            )}
            <button
              onClick={() => onEngineChange(originalEngine)}
              disabled={!engineDirty}
              className="ml-auto text-[10px] text-gray-400 hover:text-gray-200 disabled:opacity-40"
            >
              reset
            </button>
          </div>
          <select
            value={engineOverride}
            onChange={(e) => onEngineChange(e.target.value)}
            className="w-full bg-black border border-gray-700 rounded p-2 text-xs font-mono text-gray-100"
          >
            <option value="claude">claude</option>
            <option value="codex">codex</option>
          </select>
          <p className="mt-1 text-[10px] text-gray-500">
            Replays on this engine. The plain Replay button always uses the
            original attempt's engine ({originalEngine || "default"}); switch
            here to A/B the other engine on the same inputs.
          </p>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-gray-400 text-[10px] uppercase">Prompt template</span>
            {promptDirty && (
              <span className="text-amber-300 text-[10px]">● modified</span>
            )}
            <button
              onClick={() => onPromptChange(originalPrompt)}
              disabled={!promptDirty}
              className="ml-auto text-[10px] text-gray-400 hover:text-gray-200 disabled:opacity-40"
            >
              reset
            </button>
          </div>
          <textarea
            value={promptOverride}
            onChange={(e) => onPromptChange(e.target.value)}
            rows={6}
            className="w-full bg-black border border-gray-700 rounded p-2 text-xs font-mono text-gray-100"
          />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-gray-400 text-[10px] uppercase">State packet (JSON)</span>
            {packetDirty && (
              <span className="text-amber-300 text-[10px]">● modified</span>
            )}
            <button
              onClick={() => onPacketChange(originalPacket)}
              disabled={!packetDirty}
              className="ml-auto text-[10px] text-gray-400 hover:text-gray-200 disabled:opacity-40"
            >
              reset
            </button>
          </div>
          <textarea
            value={packetOverride}
            onChange={(e) => onPacketChange(e.target.value)}
            rows={6}
            className="w-full bg-black border border-gray-700 rounded p-2 text-xs font-mono text-gray-100"
          />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-gray-400 text-[10px] uppercase">Output schema (JSON)</span>
            {schemaDirty && (
              <span className="text-amber-300 text-[10px]">● modified</span>
            )}
            <button
              onClick={() => onSchemaChange(originalSchema)}
              disabled={!schemaDirty}
              className="ml-auto text-[10px] text-gray-400 hover:text-gray-200 disabled:opacity-40"
            >
              reset
            </button>
          </div>
          <textarea
            value={schemaOverride}
            onChange={(e) => onSchemaChange(e.target.value)}
            rows={6}
            className="w-full bg-black border border-gray-700 rounded p-2 text-xs font-mono text-gray-100"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onReplay}
            disabled={replaying || !anyDirty}
            className="text-xs bg-amber-700/50 border border-amber-600 hover:bg-amber-700/70 rounded px-3 py-1 text-amber-100 disabled:opacity-40"
          >
            {replaying ? "↻ replaying…" : "↻ Replay with overrides"}
          </button>
          <span className="text-[10px] text-gray-500">
            {anyDirty
              ? "Will submit modified field(s) only; unchanged fields fall through to the captured values."
              : "No changes — replay will run with the captured input. Use the plain Replay button if that's what you want."}
          </span>
        </div>
      </div>
    </div>
  );
}

// AttemptDiffView renders attempt 1 (left) vs attempt 2 (right) side-by-side.
// Three rows: Packet diff, Response diff, Feedback (read-only, shows what the
// engine told the LLM to fix). Each side is independently scrollable.
//
// JSON-aware: when both sides parse as JSON, we pretty-print them with sorted
// keys + 2-space indent BEFORE line-diffing. Sorting keys eliminates noisy
// "key-order changed" deltas — the diff highlights only real value changes.
// When parsing fails (e.g. truncated GCS-overflow strings), we fall back to
// raw line-diff. Per item 9 of the trace-view refactor.
function AttemptDiffView({ left, right }: { left: AttemptTrace; right: AttemptTrace }) {
  const leftPrompt = left.prompt_sent_ref ? `[GCS ref: ${left.prompt_sent_ref}]` : prettyJSON(left.prompt_sent);
  const rightPrompt = right.prompt_sent_ref ? `[GCS ref: ${right.prompt_sent_ref}]` : prettyJSON(right.prompt_sent);
  const leftResp = left.raw_response_ref ? `[GCS ref: ${left.raw_response_ref}]` : prettyJSON(left.raw_response);
  const rightResp = right.raw_response_ref ? `[GCS ref: ${right.raw_response_ref}]` : prettyJSON(right.raw_response);
  return (
    <div className="space-y-3">
      <DiffSection label="Packet (sorted-key pretty JSON)" left={leftPrompt} right={rightPrompt} />
      <DiffSection label="Response (sorted-key pretty JSON)" left={leftResp} right={rightResp} />
      <div>
        <div className="text-gray-400 mb-1">Retry feedback (engine → LLM after attempt 1):</div>
        <div className="bg-amber-950/40 border border-amber-700/40 p-1 text-amber-200">
          {left.feedback || "(empty)"}
        </div>
      </div>
    </div>
  );
}

// prettyJSON canonicalizes a JSON string so attempt 1 vs attempt 2 diffs
// don't surface noise from key ordering. Sorts object keys recursively
// and pretty-prints with 2-space indent. Returns the original string
// untouched when parsing fails (e.g. raw-response truncation marker).
function prettyJSON(s: string): string {
  if (!s) return "";
  try {
    return JSON.stringify(JSON.parse(s), sortKeysReplacer, 2);
  } catch {
    return s;
  }
}

function sortKeysReplacer(_key: string, value: unknown): unknown {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const sorted: Record<string, unknown> = {};
    for (const k of Object.keys(value as object).sort()) {
      sorted[k] = (value as Record<string, unknown>)[k];
    }
    return sorted;
  }
  return value;
}

function DiffSection({ label, left, right }: { label: string; left: string; right: string }) {
  const segments = diffLines(left, right);
  return (
    <div>
      <div className="text-gray-400 mb-1">{label}:</div>
      <div className="grid grid-cols-2 gap-2">
        <DiffPane segments={segments} side="left" />
        <DiffPane segments={segments} side="right" />
      </div>
    </div>
  );
}

function DiffPane({ segments, side }: { segments: DiffSegment[]; side: "left" | "right" }) {
  // Left pane shows eq + del lines (the original); right shows eq + add
  // (the corrected). Coloring: del = rose, add = emerald, eq = neutral.
  // Empty-state guard prevents a totally blank pane.
  const visible = segments.filter((s) =>
    side === "left" ? s.kind !== "add" : s.kind !== "del",
  );
  if (visible.length === 0) {
    return <div className="bg-gray-900 p-1 text-gray-500">(empty)</div>;
  }
  return (
    <div className="bg-gray-900 p-1 overflow-x-auto">
      {visible.map((seg, i) => {
        let cls = "text-ink-200";
        if (seg.kind === "add") cls = "bg-emerald-900/50 text-emerald-200";
        else if (seg.kind === "del") cls = "bg-rose-900/50 text-rose-200";
        return (
          <div key={i} className={cls}>
            {seg.text || " "}
          </div>
        );
      })}
    </div>
  );
}
